'use client';

import { useEffect, useState } from 'react';

import { ArrowRight, ExternalLink, Newspaper } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

import { BLOG_RSS_CONFIG } from '../lib/blog-content';
import { formatPostDate, parseRssFeed } from '../lib/rss';
import type { BlogRssPost } from '../types';

function RssSkeleton() {
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <Card key={index} className="animate-pulse overflow-hidden">
          <div className="aspect-video bg-slate-200" />
          <CardHeader>
            <div className="h-5 w-3/4 rounded bg-slate-200" />
            <div className="mt-2 h-4 w-1/3 rounded bg-slate-200" />
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="h-4 rounded bg-slate-200" />
              <div className="h-4 w-5/6 rounded bg-slate-200" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RssFallback() {
  return (
    <div className="rounded-xl border bg-slate-50 p-8 text-center">
      <Newspaper className="mx-auto h-10 w-10 text-slate-400" />
      <h3 className="mt-4 text-lg font-semibold text-slate-900">Could not load latest posts</h3>
      <p className="mt-2 text-slate-600">
        Visit The Resume Whisperer for the newest career tips and guides.
      </p>
      <Button asChild className="mt-6 bg-teal-600 hover:bg-teal-700" variant="default">
        <a href={BLOG_RSS_CONFIG.fallbackUrl} target="_blank" rel="noopener noreferrer">
          Read Our Blog
          <ExternalLink className="ml-2 h-4 w-4" />
        </a>
      </Button>
    </div>
  );
}

export function BlogRssFeed() {
  const [posts, setPosts] = useState<BlogRssPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchFeed() {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);

        const response = await fetch(BLOG_RSS_CONFIG.feedUrl, {
          signal: controller.signal,
          headers: { Accept: 'application/json' },
        });

        clearTimeout(timeout);

        if (!response.ok) {
          throw new Error(`RSS fetch failed: ${response.status}`);
        }

        const data = await response.json();
        const parsed = parseRssFeed(data, BLOG_RSS_CONFIG.maxPosts);

        if (!cancelled) {
          setPosts(parsed);
        }
      } catch {
        if (!cancelled) {
          setError(true);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchFeed();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="latest-posts" className="bg-white py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-10 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <span className="mb-2 inline-block text-sm font-semibold uppercase tracking-wider text-teal-600">
              From The Resume Whisperer
            </span>
            <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">
              Latest Blog Posts
            </h2>
            <p className="mt-2 text-lg text-slate-600">
              Fresh career advice, resume strategies, and job-market insights from our blog.
            </p>
          </div>
          <Button
            asChild
            variant="outline"
            className="border-teal-600 text-teal-600 hover:bg-teal-50"
          >
            <a href={BLOG_RSS_CONFIG.fallbackUrl} target="_blank" rel="noopener noreferrer">
              View All Posts
              <ArrowRight className="ml-2 h-4 w-4" />
            </a>
          </Button>
        </div>

        {loading && <RssSkeleton />}

        {!loading && error && <RssFallback />}

        {!loading && !error && posts.length === 0 && <RssFallback />}

        {!loading && !error && posts.length > 0 && (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {posts.map((post, index) => (
              <Card
                key={`${post.link}-${index}`}
                className="flex h-full flex-col overflow-hidden transition-shadow hover:shadow-md"
              >
                <a
                  href={post.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block aspect-[16/10] w-full overflow-hidden bg-slate-100"
                >
                  {post.imageUrl ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={post.imageUrl}
                      alt={post.title}
                      className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200">
                      <Newspaper className="h-10 w-10 text-slate-400" />
                    </div>
                  )}
                </a>
                <CardHeader>
                  <CardTitle className="line-clamp-2 text-lg leading-snug">
                    <a
                      href={post.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-teal-600 hover:underline"
                    >
                      {post.title}
                    </a>
                  </CardTitle>
                  <CardDescription>{formatPostDate(post.pubDate) || 'Recent post'}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-1 flex-col">
                  <p className="line-clamp-3 flex-1 text-sm text-slate-600">{post.excerpt}</p>
                  <a
                    href={post.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center text-sm font-semibold text-teal-600 hover:underline"
                  >
                    Read post
                    <ArrowRight className="ml-1 h-3 w-3" />
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
